import { Shell } from '@/components/Shell';
import { LocationForm } from '@/components/locations/LocationForm';
import { ConfirmButton } from '@/components/ui/ConfirmButton';
import { type LocationRow, adminApi } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/locations')({
  beforeLoad: async () => {
    try {
      const me = await adminApi.me();
      if (me.role !== 'admin') throw redirect({ to: '/login' });
    } catch {
      throw redirect({ to: '/login' });
    }
  },
  component: LocationsPage,
});

function LocationsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'locations'],
    queryFn: () => adminApi.locations(),
  });
  const [editing, setEditing] = useState<LocationRow | 'new' | null>(null);

  const remove = useMutation({
    mutationFn: (id: string) => adminApi.deleteLocation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'locations'] }),
  });

  const count = data?.items.length ?? 0;

  return (
    <Shell page="LOCAIS" sub={`LEVANTAMENTO E DEVOLUÇÃO · ${count} CONFIGURADOS`}>
      {/* Header actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button type="button" onClick={() => setEditing('new')} style={btnPrimary}>
          + Novo local
        </button>
      </div>

      <div style={{ background: '#fff', border: '1px solid var(--lv-line)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--lv-night)' }}>
              {['NOME', 'MORADA', 'EUR', 'CVE', 'ACTIVO', 'ACÇÕES'].map((h) => (
                <th
                  key={h}
                  className="label"
                  style={{
                    color: 'var(--lv-brand-glow)',
                    padding: '12px 16px',
                    textAlign: h === 'EUR' || h === 'CVE' ? 'right' : 'left',
                    fontSize: 10,
                    fontWeight: 800,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} style={{ padding: 20, color: 'var(--lv-gray-500)', fontSize: 12 }}>
                  A carregar…
                </td>
              </tr>
            )}
            {!isLoading && data?.items.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: 20, color: 'var(--lv-gray-500)', fontSize: 12 }}>
                  Sem locais. Adicione o primeiro acima.
                </td>
              </tr>
            )}
            {data?.items.map((l) => (
              <tr key={l.id} style={{ borderTop: '1px solid var(--lv-line)' }}>
                <td style={{ padding: '13px 16px' }}>
                  <button
                    type="button"
                    onClick={() => setEditing(l)}
                    style={{
                      background: 'transparent',
                      border: 0,
                      padding: 0,
                      color: 'var(--lv-brand)',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: 13,
                      textAlign: 'left',
                    }}
                  >
                    {l.nameLocale || '—'}
                  </button>
                </td>
                <td style={{ padding: '13px 16px', fontSize: 12, color: 'var(--lv-gray-500)' }}>
                  {l.addressLocale || '—'}
                </td>
                <td style={{ padding: '13px 16px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  <span className="display" style={{ fontSize: 18 }}>
                    {l.eurFee.toFixed(2)}
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--lv-gray-500)', marginLeft: 3 }}>
                    EUR
                  </span>
                </td>
                <td
                  style={{
                    padding: '13px 16px',
                    fontSize: 12,
                    color: 'var(--lv-gray-500)',
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {l.cveFee.toFixed(0)} CVE
                </td>
                <td style={{ padding: '13px 16px' }}>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      letterSpacing: '0.08em',
                      color: l.active ? 'var(--lv-ok)' : 'var(--lv-gray-500)',
                    }}
                  >
                    ● {l.active ? 'ACTIVO' : 'INACTIVO'}
                  </span>
                </td>
                <td style={{ padding: '13px 16px', display: 'flex', gap: 6 }}>
                  <button type="button" onClick={() => setEditing(l)} style={btnSmall}>
                    Editar
                  </button>
                  <ConfirmButton
                    label="Eliminar"
                    question={`Eliminar "${l.nameLocale}"?`}
                    onConfirm={() => remove.mutate(l.id)}
                    style={btnSmallDanger}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <LocationForm
          location={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['admin', 'locations'] });
            setEditing(null);
          }}
        />
      )}
    </Shell>
  );
}

const btnPrimary: React.CSSProperties = {
  background: 'var(--lv-brand)',
  color: '#fff',
  border: 0,
  padding: '10px 20px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
const btnSmall: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--lv-line)',
  color: 'var(--lv-ink)',
  padding: '5px 10px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
const btnSmallDanger: React.CSSProperties = {
  background: '#fff',
  color: 'var(--lv-err)',
  border: '1px solid var(--lv-err)',
  padding: '5px 10px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
